import { useCallback, useEffect, useMemo, useState } from "react";import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  FlaskConical,
  Plus,
  Loader2,
  Pencil,
  Lock,
  Unlock,
  Archive,
  X,
  Check,
  Pause,
  Play,
  AlertTriangle,
  Trophy,
  Eye,
} from "lucide-react";

/**
 * Admin A/B test creator + manager.
 *
 * Workflow:
 *   1. Lists every test from /api/admin/ab-tests (running, paused, locked, archived toggle).
 *   2. Click a row → side drawer with editable subject templates + variant descriptions,
 *      Pause/Resume, Lock-as-winner, Reset, Archive. Save patches via PATCH.
 *   3. Click "New A/B test" → modal form to create. test_id must be unique snake_case.
 */
export default function AdminAbTests() {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [selected, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const refresh = useCallback(async (preserveSelectionId) => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/ab-tests${includeArchived ? "?include_archived=true" : ""}`);
      setTests(r.data || []);
      if (preserveSelectionId) {
        const fresh = (r.data || []).find((t) => t.test_id === preserveSelectionId);
        if (fresh) setSelected(fresh);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load A/B tests");
    } finally {
      setLoading(false);
    }
  }, [includeArchived]);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="admin-ab-tests-page">
      <header className="flex items-start justify-between gap-3 mb-5 flex-wrap">
        <div>
          <div className="label-eyebrow flex items-center gap-1.5 mb-1.5">
            <FlaskConical size={11} /> Email Ops
          </div>
          <h1 className="font-display font-extrabold text-3xl lg:text-4xl tracking-tight">A/B test creator</h1>
          <p className="text-[var(--ink-2)] mt-2 max-w-2xl leading-relaxed">
            Run subject-line experiments on any transactional email without touching code.
            Templates support <code className="font-mono text-xs bg-slate-100 px-1 rounded">{`{variable}`}</code> placeholders that
            funnels fill in at send time.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <label className="text-xs text-[var(--ink-2)] flex items-center gap-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
              className="accent-[var(--primary)]"
              data-testid="ab-include-archived-toggle"
            />
            Show archived
          </label>
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="btn btn-primary"
            data-testid="ab-new-test-btn"
          >
            <Plus size={14} /> New A/B test
          </button>
        </div>
      </header>

      {loading ? (
        <div className="text-center text-sm text-[var(--ink-3)] py-12">
          <Loader2 size={16} className="animate-spin inline" /> Loading…
        </div>
      ) : tests.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="card !p-0 overflow-hidden" data-testid="ab-tests-list">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-[10px] uppercase tracking-wide text-[var(--ink-3)] font-bold">
                <th className="text-left px-4 py-2">Test</th>
                <th className="text-left px-4 py-2">Kind</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-left px-4 py-2">Winner</th>
                <th className="text-right px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {tests.map((t) => (
                <tr
                  key={t.test_id}
                  className={"border-t border-slate-100 hover:bg-slate-50 cursor-pointer " + (t.status === "archived" ? "opacity-50" : "")}
                  onClick={() => setSelected(t)}
                  data-testid={`ab-row-${t.test_id}`}
                >
                  <td className="px-4 py-3">
                    <div className="font-semibold">{t.label}</div>
                    <div className="text-[11px] text-[var(--ink-3)] font-mono">{t.test_id}</div>
                  </td>
                  <td className="px-4 py-3"><code className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">{t.kind}</code></td>
                  <td className="px-4 py-3"><StatusBadge status={t.status} /></td>
                  <td className="px-4 py-3">
                    {t.winner_variant ? (
                      <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold text-xs">
                        <Trophy size={11} /> Variant {t.winner_variant}
                      </span>
                    ) : <span className="text-[var(--ink-3)] text-xs">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setSelected(t); }}
                      className="text-[var(--primary)] text-xs font-semibold hover:underline inline-flex items-center gap-1"
                      data-testid={`ab-open-${t.test_id}`}
                    >
                      <Eye size={11} /> Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <TestDrawer
          key={selected.test_id}
          test={selected}
          onClose={() => setSelected(null)}
          onChange={(fresh) => {
            if (fresh && fresh.test_id) {
              // Optimistic update — swap the affected row in-place and update
              // the drawer's `test` prop so the UI re-renders immediately.
              setTests((prev) => prev.map((t) => (t.test_id === fresh.test_id ? fresh : t)));
              setSelected(fresh);
            } else {
              refresh(selected.test_id);
            }
          }}
        />
      )}
      {showCreate && (
        <CreateModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); refresh(); }} />
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="card text-center py-12">
      <FlaskConical size={28} className="text-[var(--ink-3)] mx-auto mb-3 opacity-40" />
      <div className="font-semibold text-[var(--ink-1)] mb-1">No A/B tests yet</div>
      <div className="text-sm text-[var(--ink-3)]">Click {"\u201CNew A/B test\u201D"} to spin up your first subject-line experiment.</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    running:  ["bg-emerald-100 text-emerald-700", "Running"],
    paused:   ["bg-amber-100  text-amber-700",   "Paused"],
    locked:   ["bg-sky-100    text-sky-700",     "Locked"],
    archived: ["bg-slate-200  text-slate-700",   "Archived"],
  };
  const [cls, label] = map[status] || ["bg-slate-100 text-slate-700", status];
  return <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${cls}`}>{label}</span>;
}

function TestDrawer({ test, onClose, onChange }) {
  const [form, setForm] = useState({
    label: test.label || "",
    description: test.description || "",
    variants: {
      A: { ...(test.variants?.A || {}) },
      B: { ...(test.variants?.B || {}) },
    },
    thresholds: { ...(test.thresholds || {}) },
  });
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(null); // name of in-flight action

  // Note: this component is keyed by `test.test_id` upstream, so React
  // remounts (not reuses) when the user picks a different row. That's why
  // we don't need a state-sync useEffect here — `useState` initialisers
  // run fresh on every mount.

  const dirty = useMemo(() => {
    if (form.label !== test.label) return true;
    if ((form.description || "") !== (test.description || "")) return true;
    for (const v of ["A", "B"]) {
      const fv = form.variants[v] || {};
      const tv = test.variants?.[v] || {};
      if ((fv.subject_template || "") !== (tv.subject_template || "")) return true;
      if ((fv.description || "") !== (tv.description || "")) return true;
    }
    return false;
  }, [form, test]);

  const save = async () => {
    setSaving(true);
    try {
      const patch = {
        label: form.label,
        description: form.description,
        variants: {
          A: { subject_template: form.variants.A.subject_template, description: form.variants.A.description },
          B: { subject_template: form.variants.B.subject_template, description: form.variants.B.description },
        },
      };
      const r = await api.patch(`/admin/ab-tests/${test.test_id}`, patch);
      toast.success("Saved");
      onChange(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const action = async (path, label, body) => {
    setBusy(label);
    try {
      const r = await api.post(`/admin/ab-tests/${test.test_id}/${path}`, body || {});
      // Optimistic update — the server returns the full new doc, so we can
      // refresh the drawer immediately without waiting on the list re-fetch.
      onChange(r.data);
      toast.success(`${label} done`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || `${label} failed`);
    } finally {
      setBusy(null);
    }
  };

  const togglePause = async () => {
    const nextStatus = test.status === "paused" ? "running" : "paused";
    setBusy("pause");
    try {
      const r = await api.patch(`/admin/ab-tests/${test.test_id}`, { status: nextStatus });
      onChange(r.data);
      toast.success(nextStatus === "paused" ? "Paused" : "Resumed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(null);
    }
  };

  const readOnly = test.status === "archived";

  return (
    <div className="fixed inset-0 z-40 flex" data-testid="ab-drawer">
      <button className="flex-1 bg-black/40" type="button" onClick={onClose} aria-label="Close" />
      <div className="w-full max-w-xl bg-white shadow-2xl flex flex-col">
        <div className="p-5 border-b flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-wide text-[var(--ink-3)] font-bold">{test.kind}</div>
            <h2 className="font-display font-extrabold text-xl mt-0.5">{test.label}</h2>
            <div className="text-xs text-[var(--ink-3)] font-mono mt-0.5">{test.test_id}</div>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded" aria-label="Close drawer">
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          <div className="flex items-center gap-2 flex-wrap">
            <StatusBadge status={test.status} />
            {test.winner_variant && (
              <span className="text-xs text-emerald-700 font-semibold inline-flex items-center gap-1">
                <Trophy size={11} /> Locked on Variant {test.winner_variant}
              </span>
            )}
          </div>

          {readOnly && (
            <div className="p-3 rounded-lg bg-slate-100 border border-slate-200 text-xs text-[var(--ink-2)] flex items-start gap-2">
              <AlertTriangle size={14} className="shrink-0 mt-0.5" />
              <div>This test is archived and read-only. Funnels fall back to the hardcoded subject lines.</div>
            </div>
          )}

          <FormField label="Label" testId="ab-label">
            <input
              type="text"
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              disabled={readOnly}
              className="w-full px-2 py-1.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-[var(--primary)] disabled:bg-slate-50"
              data-testid="ab-label-input"
            />
          </FormField>
          <FormField label="Description" testId="ab-description">
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              disabled={readOnly}
              rows={2}
              className="w-full px-2 py-1.5 rounded-lg border border-slate-200 text-sm font-mono focus:outline-none focus:border-[var(--primary)] disabled:bg-slate-50"
              data-testid="ab-description-input"
            />
          </FormField>

          {test.context_keys && test.context_keys.length > 0 && (
            <div className="bg-sky-50 border border-sky-200 rounded-lg p-2.5 text-xs">
              <div className="font-bold text-sky-900 mb-1 uppercase tracking-wide text-[10px]">Available variables</div>
              <div className="flex flex-wrap gap-1">
                {test.context_keys.map((k) => (
                  <code key={k} className="font-mono bg-white border border-sky-200 px-1.5 py-0.5 rounded text-sky-800">{`{${k}}`}</code>
                ))}
              </div>
            </div>
          )}

          {["A", "B"].map((v) => (
            <div key={v} className="border border-slate-200 rounded-lg p-3 space-y-2" data-testid={`ab-variant-${v}`}>
              <div className="flex items-center justify-between">
                <div className="font-display font-extrabold text-base">Variant {v}</div>
                {test.winner_variant === v && (
                  <span className="text-[10px] font-bold text-emerald-700 uppercase">Winner</span>
                )}
              </div>
              <FormField label="Subject template">
                <input
                  type="text"
                  value={form.variants[v]?.subject_template || ""}
                  onChange={(e) => setForm({ ...form, variants: { ...form.variants, [v]: { ...form.variants[v], subject_template: e.target.value } } })}
                  disabled={readOnly}
                  className="w-full px-2 py-1.5 rounded-lg border border-slate-200 text-sm font-mono focus:outline-none focus:border-[var(--primary)] disabled:bg-slate-50"
                  data-testid={`ab-variant-${v}-subject`}
                />
              </FormField>
              <FormField label="Description / hypothesis">
                <textarea
                  value={form.variants[v]?.description || ""}
                  onChange={(e) => setForm({ ...form, variants: { ...form.variants, [v]: { ...form.variants[v], description: e.target.value } } })}
                  disabled={readOnly}
                  rows={2}
                  className="w-full px-2 py-1.5 rounded-lg border border-slate-200 text-xs focus:outline-none focus:border-[var(--primary)] disabled:bg-slate-50"
                  data-testid={`ab-variant-${v}-desc`}
                />
              </FormField>
            </div>
          ))}
        </div>

        {!readOnly && (
          <div className="p-4 border-t flex items-center gap-2 flex-wrap bg-slate-50">
            <button
              type="button"
              onClick={save}
              disabled={!dirty || saving}
              className="btn btn-primary"
              data-testid="ab-save-btn"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              Save changes
            </button>

            {test.status === "locked" ? (
              <button
                type="button"
                onClick={() => action("reset", "reset")}
                disabled={busy === "reset"}
                className="btn btn-ghost"
                data-testid="ab-reset-btn"
              >
                {busy === "reset" ? <Loader2 size={14} className="animate-spin" /> : <Unlock size={14} />}
                Unlock (reopen)
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={togglePause}
                  disabled={busy === "pause"}
                  className="btn btn-ghost"
                  data-testid="ab-pause-btn"
                >
                  {test.status === "paused" ? <Play size={14} /> : <Pause size={14} />}
                  {test.status === "paused" ? "Resume" : "Pause"}
                </button>
                <LockButtons onLock={(variant) => action("lock", "lock", { variant })} busy={busy === "lock"} />
              </>
            )}

            <div className="ml-auto">
              <button
                type="button"
                onClick={() => action("archive", "archive")}
                disabled={busy === "archive"}
                className="btn btn-ghost text-rose-700"
                data-testid="ab-archive-btn"
              >
                {busy === "archive" ? <Loader2 size={14} className="animate-spin" /> : <Archive size={14} />}
                Archive
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function LockButtons({ onLock, busy }) {
  return (
    <div className="inline-flex items-center gap-1">
      <span className="text-xs text-[var(--ink-2)]">Lock winner:</span>
      <button type="button" disabled={busy} onClick={() => onLock("A")} className="btn btn-ghost !py-1 !px-2 !text-xs" data-testid="ab-lock-a">
        {busy ? <Loader2 size={12} className="animate-spin" /> : <Lock size={12} />} A
      </button>
      <button type="button" disabled={busy} onClick={() => onLock("B")} className="btn btn-ghost !py-1 !px-2 !text-xs" data-testid="ab-lock-b">
        {busy ? <Loader2 size={12} className="animate-spin" /> : <Lock size={12} />} B
      </button>
    </div>
  );
}

function FormField({ label, testId, children }) {
  return (
    <div data-testid={testId}>
      <label className="text-[10px] uppercase tracking-wide text-[var(--ink-3)] font-bold block mb-1">{label}</label>
      {children}
    </div>
  );
}

const EMAIL_KINDS = [
  "alert_go", "alert_maybe", "welcome", "digest",
  "weekly_engagement", "streak_saver", "podium",
  "buyer_nudge", "nudges_paused", "activation_nudge", "help_me_start",
  "subscription_confirmation", "invoice_receipt", "payment_failed", "trial_ending",
  "plan_upgraded", "plan_downgraded", "subscription_canceled", "renewal_reminder",
  "resources_hub", "track_completed",
];

function CreateModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    test_id: "",
    kind: "alert_go",
    label: "",
    description: "",
    variant_a: "",
    variant_b: "",
    context_keys: "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.test_id.trim()) return toast.error("test_id is required");
    if (!/^[a-z0-9_]+$/.test(form.test_id)) return toast.error("test_id must be snake_case (lowercase, digits, underscores).");
    if (!form.label.trim()) return toast.error("Label is required");
    if (!form.variant_a.trim() || !form.variant_b.trim()) return toast.error("Both variant subjects are required");
    setSaving(true);
    try {
      const body = {
        test_id: form.test_id.trim(),
        kind: form.kind,
        label: form.label.trim(),
        description: form.description.trim(),
        variants: {
          A: { subject_template: form.variant_a },
          B: { subject_template: form.variant_b },
        },
        context_keys: form.context_keys
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
      };
      await api.post("/admin/ab-tests", body);
      toast.success("Test created");
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="ab-create-modal">
      <form onSubmit={submit} className="bg-white rounded-xl shadow-2xl w-full max-w-lg flex flex-col max-h-[90vh]">
        <div className="p-4 border-b flex items-center justify-between">
          <div className="font-display font-extrabold text-lg">New A/B test</div>
          <button type="button" onClick={onClose} className="p-1 hover:bg-slate-100 rounded">
            <X size={16} />
          </button>
        </div>
        <div className="p-4 space-y-3 overflow-y-auto">
          <FormField label="test_id (snake_case, unique)" testId="ab-create-test-id">
            <input
              type="text"
              value={form.test_id}
              onChange={(e) => setForm({ ...form, test_id: e.target.value })}
              placeholder="e.g. welcome_subject_v2"
              pattern="[a-z0-9_]+"
              className="w-full px-2 py-1.5 rounded-lg border border-slate-200 text-sm font-mono focus:outline-none focus:border-[var(--primary)]"
              data-testid="ab-create-test-id-input"
              required
            />
          </FormField>
          <FormField label="Email kind">
            <select
              value={form.kind}
              onChange={(e) => setForm({ ...form, kind: e.target.value })}
              className="w-full px-2 py-1.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-[var(--primary)]"
              data-testid="ab-create-kind"
            >
              {EMAIL_KINDS.map((k) => (
                <option key={k} value={k}>{k}</option>
              ))}
            </select>
          </FormField>
          <FormField label="Label (human-readable)">
            <input
              type="text"
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder="e.g. Welcome — short vs long subject"
              className="w-full px-2 py-1.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-[var(--primary)]"
              data-testid="ab-create-label"
              required
            />
          </FormField>
          <FormField label="Description / hypothesis">
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={2}
              placeholder="What are you testing and why?"
              className="w-full px-2 py-1.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-[var(--primary)]"
              data-testid="ab-create-description"
            />
          </FormField>
          <FormField label="Variant A subject template">
            <input
              type="text"
              value={form.variant_a}
              onChange={(e) => setForm({ ...form, variant_a: e.target.value })}
              placeholder='e.g. "GO · {address} — {spread_fmt} spread"'
              className="w-full px-2 py-1.5 rounded-lg border border-slate-200 text-sm font-mono focus:outline-none focus:border-[var(--primary)]"
              data-testid="ab-create-variant-a"
              required
            />
          </FormField>
          <FormField label="Variant B subject template">
            <input
              type="text"
              value={form.variant_b}
              onChange={(e) => setForm({ ...form, variant_b: e.target.value })}
              placeholder='e.g. "🔥 {spread_fmt} spread waiting on {street}"'
              className="w-full px-2 py-1.5 rounded-lg border border-slate-200 text-sm font-mono focus:outline-none focus:border-[var(--primary)]"
              data-testid="ab-create-variant-b"
              required
            />
          </FormField>
          <FormField label="Variables (comma-separated, optional)">
            <input
              type="text"
              value={form.context_keys}
              onChange={(e) => setForm({ ...form, context_keys: e.target.value })}
              placeholder="e.g. address, street, spread_fmt"
              className="w-full px-2 py-1.5 rounded-lg border border-slate-200 text-sm font-mono focus:outline-none focus:border-[var(--primary)]"
              data-testid="ab-create-context-keys"
            />
            <div className="text-[10px] text-[var(--ink-3)] mt-1">
              For documentation only — the funnel passes whatever it needs. Use <code className="font-mono">{`{var}`}</code> syntax in subject templates.
            </div>
          </FormField>
        </div>
        <div className="p-4 border-t flex items-center justify-end gap-2 bg-slate-50">
          <button type="button" onClick={onClose} className="btn btn-ghost" data-testid="ab-create-cancel">Cancel</button>
          <button type="submit" disabled={saving} className="btn btn-primary" data-testid="ab-create-submit">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            Create
          </button>
        </div>
      </form>
    </div>
  );
}
